/**
 * 组件订阅全局 store 的唯一入口。
 *
 * 用法：`const unread = useGlobal((s) => s.conversations[key]?.unread ?? 0)`
 * 选择器返回现拼对象时传 shallowEqual：`useGlobal(sel, shallowEqual)`。
 *
 * 选择器/equals 允许是内联闭包（每次渲染新引用）：订阅用 ref 永远取最新一份，
 * 不因闭包变化重订阅。
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import { globalStore, type GlobalState } from './global';
import type { Equals, Store } from './store';

export function useStoreSelector<S extends object, T>(
  store: Store<S>,
  selector: (state: S) => T,
  equals: Equals<T> = Object.is,
): T {
  const [, setTick] = useState(0);
  const value = selector(store.get());
  const latestRef = useRef({ selector, equals, value });
  // 渲染期写 ref 是 use-sync-external-store shim 的标准写法：订阅回调必须拿到
  // 本次渲染的 selector/value，挪进 effect 会留下「变更发生在渲染后、effect 前」的竞态窗口。
  // eslint-disable-next-line react-hooks/refs
  latestRef.current = { selector, equals, value };

  useEffect(() => {
    const check = (): void => {
      const l = latestRef.current;
      const next = l.selector(store.get());
      if (!l.equals(l.value, next)) {
        setTick((t) => t + 1);
      }
    };
    // 本次渲染取值到订阅生效之间 store 可能已变化，先补查一次
    check();
    // equals 恒 false = 每次 flush 都回调，由 check 用最新选择器自行比较
    return store.subscribe((s) => s, check, () => false);
  }, [store]);

  return value;
}

export function useGlobal<T>(selector: (state: GlobalState) => T, equals?: Equals<T>): T {
  return useStoreSelector(globalStore(), selector, equals ?? Object.is);
}
